import { createTheme } from '@mui/material/styles'
import { prefixer } from 'stylis'
import rtlPlugin from 'stylis-plugin-rtl'
import createCache from '@emotion/cache'

// Font family options
export const FONT_FAMILIES = [
    { value: 'Inter', label: 'Inter', stack: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif' },
    { value: 'Roboto', label: 'Roboto', stack: '"Roboto", -apple-system, BlinkMacSystemFont, sans-serif' },
    { value: 'Poppins', label: 'Poppins', stack: '"Poppins", -apple-system, BlinkMacSystemFont, sans-serif' },
    { value: 'Open Sans', label: 'Open Sans', stack: '"Open Sans", -apple-system, BlinkMacSystemFont, sans-serif' },
    { value: 'Montserrat', label: 'Montserrat', stack: '"Montserrat", -apple-system, BlinkMacSystemFont, sans-serif' },
    { value: 'Lato', label: 'Lato', stack: '"Lato", -apple-system, BlinkMacSystemFont, sans-serif' },
    { value: 'Cairo', label: 'Cairo (Arabic)', stack: '"Cairo", -apple-system, BlinkMacSystemFont, sans-serif' },
    { value: 'Tajawal', label: 'Tajawal (Arabic)', stack: '"Tajawal", -apple-system, BlinkMacSystemFont, sans-serif' },
]

// Helper to lighten/darken colors
const adjustColor = (color, amount) => {
    const clamp = (num) => Math.min(255, Math.max(0, num))
    const hex = color.replace('#', '')
    const r = clamp(parseInt(hex.slice(0, 2), 16) + amount)
    const g = clamp(parseInt(hex.slice(2, 4), 16) + amount)
    const b = clamp(parseInt(hex.slice(4, 6), 16) + amount)
    return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`
}

/**
 * Create RTL cache for Arabic
 */
export const createEmotionCache = (direction = 'ltr') => {
    if (direction === 'rtl') {
        return createCache({
            key: 'muirtl',
            stylisPlugins: [prefixer, rtlPlugin],
        })
    }
    return createCache({ key: 'muiltr' })
}

/**
 * Create MUI theme with RTL support and custom settings
 */
export const createAppTheme = (direction = 'ltr', darkMode = false, customSettings = {}) => {
    // Default values
    const primaryColor = customSettings.primaryColor || '#1a3a6b'
    const secondaryColor = customSettings.secondaryColor || '#c9a227'
    const successColor = customSettings.successColor || '#10b981'
    const warningColor = customSettings.warningColor || '#f59e0b'
    const errorColor = customSettings.errorColor || '#ef4444'
    const fontFamily = customSettings.fontFamily || 'Inter'
    const borderRadius = customSettings.borderRadius || 12

    // Get font stack
    const fontConfig = FONT_FAMILIES.find(f => f.value === fontFamily) || FONT_FAMILIES[0]
    const fontStack = direction === 'rtl'
        ? `"Cairo", ${fontConfig.stack}`
        : `${fontConfig.stack}, "Cairo"`

    return createTheme({
        direction,
        palette: {
            mode: darkMode ? 'dark' : 'light',
            primary: {
                main: primaryColor,
                light: adjustColor(primaryColor, 40),
                dark: adjustColor(primaryColor, -30),
                contrastText: '#ffffff',
            },
            secondary: {
                main: secondaryColor,
                light: adjustColor(secondaryColor, 40),
                dark: adjustColor(secondaryColor, -30),
                contrastText: '#ffffff',
            },
            success: {
                main: successColor,
                light: adjustColor(successColor, 40),
                dark: adjustColor(successColor, -30),
            },
            warning: {
                main: warningColor,
                light: adjustColor(warningColor, 40),
                dark: adjustColor(warningColor, -30),
            },
            error: {
                main: errorColor,
                light: adjustColor(errorColor, 40),
                dark: adjustColor(errorColor, -30),
            },
            info: {
                main: '#3b82f6',
                light: '#60a5fa',
                dark: '#2563eb',
            },
            background: {
                default: darkMode ? '#0f172a' : '#f8fafc',
                paper: darkMode ? '#1e293b' : '#ffffff',
            },
            text: {
                primary: darkMode ? '#f1f5f9' : '#1e293b',
                secondary: darkMode ? '#94a3b8' : '#64748b',
            },
            divider: darkMode ? 'rgba(148, 163, 184, 0.12)' : 'rgba(0, 0, 0, 0.08)',
        },
        typography: {
            fontFamily: fontStack,
            h1: {
                fontWeight: 700,
                fontSize: '2.5rem',
            },
            h2: {
                fontWeight: 600,
                fontSize: '2rem',
            },
            h3: {
                fontWeight: 600,
                fontSize: '1.5rem',
            },
            h4: {
                fontWeight: 600,
                fontSize: '1.25rem',
            },
            h5: {
                fontWeight: 600,
                fontSize: '1.125rem',
            },
            h6: {
                fontWeight: 600,
                fontSize: '1rem',
            },
            button: {
                textTransform: 'none',
                fontWeight: 500,
            },
        },
        shape: {
            borderRadius: borderRadius,
        },
        shadows: [
            'none',
            '0 1px 2px 0 rgb(0 0 0 / 0.05)',
            '0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)',
            '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
            '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)',
            '0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)',
            '0 25px 50px -12px rgb(0 0 0 / 0.25)',
            ...Array(18).fill('0 25px 50px -12px rgb(0 0 0 / 0.25)'),
        ],
        components: {
            MuiButton: {
                styleOverrides: {
                    root: {
                        borderRadius: Math.min(borderRadius, 8),
                        padding: '8px 20px',
                        fontWeight: 500,
                    },
                    contained: {
                        boxShadow: 'none',
                        '&:hover': {
                            boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
                        },
                    },
                },
            },
            MuiCard: {
                styleOverrides: {
                    root: {
                        borderRadius: borderRadius + 4,
                        boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)',
                    },
                },
            },
            MuiPaper: {
                styleOverrides: {
                    root: {
                        backgroundImage: 'none',
                    },
                },
            },
            MuiTextField: {
                defaultProps: {
                    variant: 'outlined',
                    size: 'small',
                },
                styleOverrides: {
                    root: {
                        '& .MuiOutlinedInput-root': {
                            borderRadius: Math.min(borderRadius, 8),
                        },
                    },
                },
            },
            MuiTableCell: {
                styleOverrides: {
                    head: {
                        fontWeight: 600,
                        backgroundColor: darkMode ? '#1e293b' : '#f8fafc',
                    },
                },
            },
            MuiChip: {
                styleOverrides: {
                    root: {
                        borderRadius: Math.min(borderRadius, 8),
                    },
                },
            },
            MuiDialog: {
                styleOverrides: {
                    paper: {
                        borderRadius: borderRadius + 4,
                    },
                },
            },
            MuiDrawer: {
                styleOverrides: {
                    paper: {
                        borderRight: 'none',
                        borderLeft: 'none',
                    },
                },
            },
        },
    })
}

export default createAppTheme
